import { useState, useEffect } from "react";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import {
  defaultFilteringForDailyRoutes,
  getClearDailyRoute,
  getDailyRouteFilteringOptions,
  getUserName,
} from "../../common/EntitiesManagement";
import localize from "../../common/localize";
import ErrorMessage from "../../components/common/ErrorMessage";
import ModalBackground from "../../components/common/ModalBackground";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import PagePagination from "../../components/PagePagination";
import ActionButtons from "../../components/common/ActionButtons";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import {
  GetDateInMMDDYYYYFormat,
  generateNoDataValues,
  getFiltersInfo,
} from "../../common/utils";
import useGlobalContext from "../../hooks/useGlobalContext";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import AddOrEditDailyRoute from "./components/AddOrEditDailyRoute";
import { Print } from "@mui/icons-material";
import EntityPrinter from "../../components/common/EntityPrinter";
import AccessStatus from "./components/AccessStatus";

export default function DailyRoutes() {
  const {
    dailyRouteModel,
    setDailyRouteModel,
    dailyRouteData,
    DAILY_ROUTE_ENDPOINT,
    config,
    pageIndexDailyRoute,
    setPageIndexDailyRoute,
    toggleVisibleModalConfirm,
    deleteEntity,
    basicFilterData,
    setBasicFilterOptions,
    filterForDailyRoutes,
    setFilterForDailyRoutes,
    createEntity,
    updateEntity,
  } = useGlobalContext();
  const [dailyRouteDataFiltered, setDailyRouteDataFiltered] = useState(
    dailyRouteData.Data
  );
  const userName = getUserName();
  const [deletedRouteEvents, setDeletedRouteEvents] = useState([]);
  const [updateRouteEvents, setUpdateRouteEvents] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dailyRouteModelToDelete, setDailyRouteModelToDelete] = useState();
  const [showSaveConfirmationMessage, setShowSaveConfirmationMessage] =
    useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getDailyRouteFilteringOptions());
  }, []);

  useEffect(() => {
    setDailyRouteDataFiltered(dailyRouteData.Data);
  }, [dailyRouteData]);

  const validateOperationResult = (result, isDelete) => {
    setShowAddModal(false);
    setDailyRouteModel(undefined);

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([DAILY_ROUTE_ENDPOINT, config]);

    if (isDelete) {
      if (pageIndexDailyRoute !== 0 && dailyRouteData.Data.length === 1) {
        setPageIndexDailyRoute(pageIndexDailyRoute - 1);
      }
    }

    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${DAILY_ROUTE_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const disableSaveChanges = () =>
    !dailyRouteModel?.Name ||
    dailyRouteModel?.Name.replaceAll(" ", "").length <= 0 ||
    dailyRouteModel?.RouteEvents?.length <= 0;

  const handleUpdateRequest = async (data) => {
    const newEvents = dailyRouteModel.RouteEvents.filter((x) => x.IsNew);

    const updateResult = await updateEntity(
      `${`${DAILY_ROUTE_ENDPOINT}/${data.Id}`}`,
      {
        ...data,
        RouteEvents: [...newEvents],
        ToUpdate: updateRouteEvents.map((x) => ({
          id: x.Id,
          observation: x.Observation,
        })),
        ToDelete: deletedRouteEvents.map((x) => x.Id),
      },
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${DAILY_ROUTE_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const getColumnTitles = () => {
    return [
      localize.route_date,
      localize.name,
      localize.edition_level,
      localize.assigned_to,
      localize.observations,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  const isPublic = (value) => {
    return value === false;
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.daily_route_title}
          report_subtitle={getFiltersInfo(filterForDailyRoutes)}
          url={DAILY_ROUTE_ENDPOINT}
          filters={filterForDailyRoutes ?? []}
          columns={getColumnTitles()}
          objectTypeName="daily_route"
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={dailyRouteModelToDelete.Name}
          entityType={localize.route}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setDailyRouteModelToDelete(undefined);
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(dailyRouteModelToDelete.Id);
            setShowDeleteModal(false);
            setDailyRouteModelToDelete(undefined);
          }}
        />
      )}

      {showAddModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showAddModal}
          setShowModal={setShowAddModal}
          title={
            dailyRouteModel?.Id && dailyRouteModel?.Id > 0
              ? localize.edit_route
              : localize.new_route
          }
          hideSubmitButton={
            dailyRouteModel.IsPrivate && dailyRouteModel.CreatedBy !== userName
          }
          Component={
            <AddOrEditDailyRoute
              showCreateConfirmation={showSaveConfirmationMessage}
              setShowCreateConfirmation={setShowSaveConfirmationMessage}
              handleCreateRequest={handleCreateRequest}
              handleUpdateRequest={handleUpdateRequest}
              setShowModal={setShowAddModal}
              dailyRoute={dailyRouteModel}
              setDailyRoute={setDailyRouteModel}
              updateRouteEvents={updateRouteEvents}
              setUpdateRouteEvents={setUpdateRouteEvents}
              setDeletedRouteEvents={setDeletedRouteEvents}
              deletedRouteEvents={deletedRouteEvents}
              isReadOnly={
                dailyRouteModel.IsPrivate &&
                dailyRouteModel.CreatedBy !== userName
              }
            />
          }
          cancelButtonText={
            dailyRouteModel.IsPrivate && dailyRouteModel.CreatedBy !== userName
              ? localize.close
              : localize.cancel
          }
          onClickToCancel={() => {
            setDailyRouteModel(undefined);
            setShowAddModal(false);
          }}
          validators={disableSaveChanges()}
          onClickToSubmit={() => {
            setShowSaveConfirmationMessage(true);
          }}
          customSecondaryAction={
            dailyRouteModel?.Id > 0
              ? {
                  name: localize.print,
                  action: () => {
                    window.open(
                      `${window.location.href.replace("#", "")}/${
                        dailyRouteModel?.Id
                      }/report`,
                      "_blank"
                    );
                  },
                }
              : undefined
          }
        />
      )}

      <div>
        <Card
          variant="outlined"
          style={{
            margin: "16px",
            boxShadow:
              "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
          }}
        >
          <CardContent>
            <PageTitleSubtitle
              title={localize.daily_route_title}
              subtitle={localize.daily_route_subtitle}
            />

            <SearcherAndAdd
              printButtonMethod={() => setShowPrinterModal(true)}
              entityName="daily_routes_filter"
              defaultFiltering={defaultFilteringForDailyRoutes(true)}
              filterOptions={getDailyRouteFilteringOptions()}
              filterData={filterForDailyRoutes}
              setFilterData={setFilterForDailyRoutes}
              handleAddBtn={() => {
                setUpdateRouteEvents([]);
                setDailyRouteModel(getClearDailyRoute(new Date()));
                setShowAddModal(true);
              }}
            />
          </CardContent>
        </Card>

        <TableComponent
          pagination={
            <PagePagination
              pageIndex={pageIndexDailyRoute}
              setPageIndex={setPageIndexDailyRoute}
              dataArray={dailyRouteData}
            />
          }
          firstColumnTitle={localize.actions}
          columnTitles={getColumnTitles()}
          results={
            dailyRouteData.Data?.length !== 0
              ? dailyRouteDataFiltered.map((item) => ({
                  key: item.Id,
                  data: [
                    <div>
                      <ActionButtons
                        buttons={[
                          {
                            name: localize.print,
                            icon: (
                              <Print
                                style={{ width: "16px", height: "16px" }}
                              />
                            ),
                            action: () => {
                              window.open(
                                `${window.location.href.replace("#", "")}/${
                                  item.Id
                                }/report`,
                                "_blank"
                              );
                            },
                          },
                        ]}
                        onClickForEditButton={() => {
                          setDailyRouteModel({
                            ...item,
                            RouteDateValue: new Date(item.RouteDate),
                            PersonalId: item.PersonalAssigned?.id,
                          });
                          setShowAddModal(true);
                        }}
                        hideEditButton={
                          item.IsPrivate && item.CreatedBy !== userName
                        }
                        hideDeleteButton={
                          item.IsPrivate && item.CreatedBy !== userName
                        }
                        detailsButtonText={
                          item.IsPrivate && item.CreatedBy !== userName
                            ? localize.details
                            : undefined
                        }
                        onClickForDetailButton={() => {
                          setDailyRouteModel({
                            ...item,
                            RouteDateValue: new Date(item.RouteDate),
                            PersonalId: item.PersonalAssigned?.id,
                          });
                          setShowAddModal(true);
                        }}
                        onclickForDeleteButton={() => {
                          setDailyRouteModelToDelete(item);
                          setShowDeleteModal(true);
                          toggleVisibleModalConfirm();
                        }}
                      />
                    </div>,
                    GetDateInMMDDYYYYFormat(new Date(item.RouteDate)),
                    <ValueOrNoDataLabel data={item.Name} />,
                    <AccessStatus isPublic={isPublic(item.IsPrivate)} />,
                    <ValueOrNoDataLabel
                      data={
                        !item.PersonalAssigned?.SurName
                          ? null
                          : `${item.PersonalAssigned.SurName} ${item.PersonalAssigned.LastName}`
                      }
                    />,
                    <ValueOrNoDataLabel data={item.Observation} />,
                    new Date(item.ModificationDate).toLocaleString(undefined, {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }),
                    item.CreatedBy,
                  ],
                }))
              : [generateNoDataValues(8)]
          }
        />
      </div>
    </div>
  );
}
